import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { format, toZonedTime } from 'date-fns-tz';
import { Loader2, Mail, Wrench, Clock, Globe, BadgeCheck, Calendar, Activity } from 'lucide-react';

interface UserDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  fullName: string;
}

interface ToolUsageRecord {
  id: string;
  tool_name: string;
  accessed_at: string;
}

interface SessionRecord {
  id: string;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number;
  pages_visited: number;
  last_page: string | null;
}

interface UserDetails {
  email: string;
  full_name: string;
  country: string | null;
  verified_at: string | null;
  created_at: string;
  emails_sent_count: number;
  total_sessions: number;
  total_duration_seconds: number;
  total_pages_visited: number;
  toolUsage: ToolUsageRecord[];
  sessions: SessionRecord[];
}

export const UserDetailModal = ({ isOpen, onClose, email, fullName }: UserDetailModalProps) => {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<UserDetails | null>(null);

  useEffect(() => {
    if (isOpen && email) {
      fetchUserDetails();
    }
  }, [isOpen, email]);

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      // Fetch tool_access record
      const { data: accessData } = await supabase
        .from("tool_access")
        .select("id, email, full_name, country, verified_at, created_at, emails_sent_count")
        .eq("email", email.toLowerCase())
        .maybeSingle();

      if (!accessData) {
        setDetails(null);
        setLoading(false);
        return;
      }

      // Fetch tool usage
      const { data: usageData } = await supabase
        .from("tool_usage")
        .select("id, tool_name, accessed_at")
        .eq("tool_access_id", accessData.id)
        .order("accessed_at", { ascending: false });

      // Fetch sessions
      const { data: sessionData } = await supabase
        .from("user_sessions")
        .select("id, started_at, ended_at, duration_seconds, pages_visited, last_page")
        .eq("tool_access_id", accessData.id)
        .order("started_at", { ascending: false });

      // Calculate totals
      const sessions = sessionData || [];
      const totalDuration = sessions.reduce((sum, s) => sum + (s.duration_seconds || 0), 0);
      const totalPages = sessions.reduce((sum, s) => sum + (s.pages_visited || 0), 0);

      setDetails({
        email: accessData.email,
        full_name: accessData.full_name,
        country: accessData.country,
        verified_at: accessData.verified_at,
        created_at: accessData.created_at,
        emails_sent_count: accessData.emails_sent_count,
        total_sessions: sessions.length,
        total_duration_seconds: totalDuration,
        total_pages_visited: totalPages,
        toolUsage: usageData || [],
        sessions: sessions,
      });
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {fullName}
            {details?.verified_at && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-success/10 text-success">
                <BadgeCheck className="w-3 h-3" />
                Verified
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : !details ? (
          <div className="text-center py-8 text-muted-foreground">
            No data found for this user
          </div>
        ) : (
          <div className="space-y-6">
            {/* Overview Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <Globe className="w-5 h-5 mx-auto mb-1 text-primary" />
                <p className="text-lg font-bold">{details.total_sessions}</p>
                <p className="text-xs text-muted-foreground">Total Visits</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <Clock className="w-5 h-5 mx-auto mb-1 text-primary" />
                <p className="text-lg font-bold">{formatDuration(details.total_duration_seconds)}</p>
                <p className="text-xs text-muted-foreground">Time Spent</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <Wrench className="w-5 h-5 mx-auto mb-1 text-primary" />
                <p className="text-lg font-bold">{details.toolUsage.length}</p>
                <p className="text-xs text-muted-foreground">Tools Used</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <Mail className="w-5 h-5 mx-auto mb-1 text-primary" />
                <p className="text-lg font-bold">{details.emails_sent_count}</p>
                <p className="text-xs text-muted-foreground">Emails Sent</p>
              </div>
            </div>

            {/* User Info */}
            <div className="bg-muted/30 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email:</span>
                <span className="font-medium">{details.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Country:</span>
                <span className="font-medium">{details.country || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">First Seen:</span>
                <span className="font-medium">
                  {format(toZonedTime(new Date(details.created_at), 'America/New_York'), "MMM d, yyyy h:mm a")}
                </span>
              </div>
              {details.verified_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Verified At:</span>
                  <span className="font-medium">
                    {format(toZonedTime(new Date(details.verified_at), 'America/New_York'), "MMM d, yyyy h:mm a")}
                  </span>
                </div>
              )}
            </div>

            {/* Activity Tabs */}
            <Tabs defaultValue="tools">
              <TabsList className="w-full">
                <TabsTrigger value="tools" className="flex-1">
                  Tool Usage ({details.toolUsage.length})
                </TabsTrigger>
                <TabsTrigger value="sessions" className="flex-1">
                  Sessions ({details.sessions.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="tools" className="mt-4">
                {details.toolUsage.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No tools used yet</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {details.toolUsage.map((usage) => (
                      <div
                        key={usage.id}
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                      >
                        <span className="inline-flex px-2 py-1 rounded-lg text-xs font-medium bg-primary/10 text-primary">
                          {usage.tool_name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {format(toZonedTime(new Date(usage.accessed_at), 'America/New_York'), "MMM d, h:mm a")}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="sessions" className="mt-4">
                {details.sessions.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No session data available</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {details.sessions.map((session) => (
                      <div
                        key={session.id}
                        className="p-3 bg-muted/30 rounded-lg text-sm"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">
                            {format(toZonedTime(new Date(session.started_at), 'America/New_York'), "MMM d, h:mm a")}
                          </span>
                          <span className="text-xs bg-muted px-2 py-0.5 rounded">
                            {formatDuration(session.duration_seconds)}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>{session.pages_visited} page{session.pages_visited !== 1 ? 's' : ''}</span>
                          {session.last_page && <span>Last: {session.last_page}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
